import React, { useState, useEffect } from "react";
import {
  Search,
  Frown,
  ThermometerSnowflake,
  Smile,
  Angry,
  Meh,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Save,
} from "lucide-react";
import { api } from "../../services/api";

export type EmotionType = "sad" | "anxious" | "happy" | "angry" | "neutral";

export interface VocabularyWord {
  id: string;
  word: string;
  emotion: EmotionType;
  language: "th" | "en";
  createdAt: Date;
}

type VocabularyApiPayload =
  | Partial<Record<EmotionType, unknown>>
  | { emotions?: Partial<Record<EmotionType, unknown>> };

type FetchVocabularyResponse = {
  success: boolean;
  data?: {
    _id?: string;
    emotions?: any;
    createdAt?: string;
    updatedAt?: string;
  };
  message?: string;
};

type UpdateVocabularyResponse = {
  success: boolean;
  message?: string;
  data?: {
    _id?: string;
    emotions?: any;
    createdAt?: string;
    updatedAt?: string;
  };
};

const emotionConfig: Record<
  EmotionType,
  {
    label: string;
    labelTh: string;
    color: string;
    bgColor: string;
    borderColor: string;
    icon: React.ElementType;
    emoji: string;
  }
> = {
  sad: {
    label: "Sad",
    labelTh: "เศร้า",
    color: "text-blue-700",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    icon: Frown,
    emoji: "😔",
  },
  anxious: {
    label: "Anxious",
    labelTh: "วิตกกังวล",
    color: "text-purple-700",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    icon: ThermometerSnowflake,
    emoji: "😰",
  },
  happy: {
    label: "Happy",
    labelTh: "มีความสุข",
    color: "text-yellow-700",
    bgColor: "bg-yellow-50",
    borderColor: "border-yellow-200",
    icon: Smile,
    emoji: "😊",
  },
  angry: {
    label: "Angry",
    labelTh: "โกรธ",
    color: "text-red-700",
    bgColor: "bg-red-50",
    borderColor: "border-red-200",
    icon: Angry,
    emoji: "😠",
  },
  neutral: {
    label: "Neutral",
    labelTh: "กลางๆ",
    color: "text-gray-700",
    bgColor: "bg-gray-50",
    borderColor: "border-gray-200",
    icon: Meh,
    emoji: "😐",
  },
};

const VocabularyManager: React.FC = () => {
  const [words, setWords] = useState<VocabularyWord[]>([]);
  const [vocabularyId, setVocabularyId] = useState<string | null>(null);
  const [expandedEmotion, setExpandedEmotion] = useState<EmotionType | null>(
    null,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [filterLanguage, setFilterLanguage] = useState<"all" | "th" | "en">(
    "all",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editContents, setEditContents] = useState<Record<EmotionType, string>>(
    {
      sad: "",
      anxious: "",
      happy: "",
      angry: "",
      neutral: "",
    },
  );
  const [isSaving, setIsSaving] = useState<Record<EmotionType, boolean>>({
    sad: false,
    anxious: false,
    happy: false,
    angry: false,
    neutral: false,
  });

  const emotions: EmotionType[] = [
    "sad",
    "anxious",
    "happy",
    "angry",
    "neutral",
  ];

  // Detect language for a word
  const detectLanguage = (word: string): "th" | "en" => {
    return /[\u0E00-\u0E7F]/.test(word) ? "th" : "en";
  };

  const normalizeVocabularyToWords = (
    payload: VocabularyApiPayload,
  ): VocabularyWord[] => {
    const maybeEmotions =
      (payload as any)?.emotions &&
      typeof (payload as any).emotions === "object"
        ? (payload as any).emotions
        : payload;

    const now = new Date();
    const out: VocabularyWord[] = [];
    const seen = new Set<string>();

    for (const emotion of emotions) {
      const val = (maybeEmotions as any)?.[emotion];
      if (!val) continue;

      // Supported shapes:
      // 1) { sad: ["sad","depressed"] }
      // 2) { sad: { en: [...], th: [...] } }
      const addWord = (raw: unknown) => {
        const w = typeof raw === "string" ? raw.trim() : "";
        if (!w) return;
        const lower = w.toLowerCase();
        const key = `${emotion}:${lower}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push({
          id: `${emotion}-${lower}`,
          word: lower,
          emotion,
          language: detectLanguage(lower),
          createdAt: now,
        });
      };

      if (Array.isArray(val)) {
        for (const raw of val) addWord(raw);
      } else if (typeof val === "object") {
        const en = (val as any).en;
        const th = (val as any).th;
        if (Array.isArray(en)) for (const raw of en) addWord(raw);
        if (Array.isArray(th)) for (const raw of th) addWord(raw);
      }
    }

    return out;
  };

  const extractEmotionMap = (
    raw: any,
  ): Partial<Record<EmotionType, string[]>> => {
    // Accept:
    // - { sad: ["sad"] }
    // - { emotions: { sad: ["sad"] } }
    // - { emotions: { emotions: { sad: ["sad"] } } }
    const candidate =
      raw?.emotions?.emotions?.emotions ??
      raw?.emotions?.emotions ??
      raw?.emotions ??
      raw;

    const out: Partial<Record<EmotionType, string[]>> = {};
    if (!candidate || typeof candidate !== "object") return out;

    const emotionsList: EmotionType[] = [
      "sad",
      "anxious",
      "happy",
      "angry",
      "neutral",
    ];

    for (const e of emotionsList) {
      const val = (candidate as any)[e];
      if (!Array.isArray(val)) continue;
      out[e] = Array.from(
        new Set(
          val
            .map((w: any) =>
              typeof w === "string" ? w.trim().toLowerCase() : "",
            )
            .filter(Boolean),
        ),
      );
    }

    return out;
  };

  const fetchVocabulary = async () => {
    // Based on backend routes:
    // GET /vocabulary/latest
    const VOCABULARY_ENDPOINT = "/backend/vocabulary/latest";

    setIsLoading(true);
    setLoadError(null);

    try {
      const res = await api.get<FetchVocabularyResponse>(VOCABULARY_ENDPOINT);
      const vocab = res?.data?.data;
      if (!vocab?._id) {
        setVocabularyId(null);
        setWords([]);
        return;
      }

      setVocabularyId(vocab._id);
      const emotionMap = extractEmotionMap(vocab?.emotions);
      const normalized = normalizeVocabularyToWords(
        emotionMap as VocabularyApiPayload,
      );

      // If backend returns empty vocabulary, show empty state.
      if (normalized.length > 0) {
        setWords(normalized);
        return;
      }
      setWords([]);
    } catch (err: any) {
      console.error("Failed to fetch vocabulary", err);
      setLoadError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch vocabulary",
      );
      setVocabularyId(null);
      setWords([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load words from API
  useEffect(() => {
    void fetchVocabulary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize edit contents when an emotion is expanded
  useEffect(() => {
    if (expandedEmotion) {
      const existingWords = words
        .filter((w) => w.emotion === expandedEmotion)
        .map((w) => w.word)
        .sort((a, b) => a.localeCompare(b));

      setEditContents((prev) => ({
        ...prev,
        [expandedEmotion]: existingWords.join(", "),
      }));
    }
  }, [expandedEmotion, words]);

  // Save (add/remove) the full comma-separated list for an emotion via API
  const handleSaveEmotionWords = async (emotion: EmotionType) => {
    setIsSaving((prev) => ({ ...prev, [emotion]: true }));
    setLoadError(null);

    if (!vocabularyId) {
      setLoadError("Vocabulary not loaded yet. Please refresh and try again.");
      setIsSaving((prev) => ({ ...prev, [emotion]: false }));
      return;
    }

    const inputText = editContents[emotion]?.trim() || "";

    if (!inputText) {
      // Allow clearing all words for this emotion
    }

    const nextEmotionWords = inputText
      ? Array.from(
          new Set(
            inputText
              .split(",")
              .map((w) => w.trim().toLowerCase())
              .filter((w) => w.length > 0),
          ),
        )
      : [];

    // Build full current emotions map (used for local fallback if backend doesn't support partial updates)
    const fullMap = extractEmotionMap({
      sad: words.filter((w) => w.emotion === "sad").map((w) => w.word),
      anxious: words.filter((w) => w.emotion === "anxious").map((w) => w.word),
      happy: words.filter((w) => w.emotion === "happy").map((w) => w.word),
      angry: words.filter((w) => w.emotion === "angry").map((w) => w.word),
      neutral: words.filter((w) => w.emotion === "neutral").map((w) => w.word),
    });
    fullMap[emotion] = nextEmotionWords;

    // Based on backend route:
    // PUT /vocabulary/:id/update  { emotions: { [emotion]: [...] } }  (preferred)
    // If backend overwrites instead of merging, we fallback to full payload.
    const UPDATE_ENDPOINT = `/backend/vocabulary/${vocabularyId}/update`;

    try {
      const tryPartialUpdate = async () => {
        return api.put<UpdateVocabularyResponse>(UPDATE_ENDPOINT, {
          emotions: { [emotion]: nextEmotionWords },
        });
      };

      const tryFullUpdate = async () => {
        return api.put<UpdateVocabularyResponse>(UPDATE_ENDPOINT, {
          emotions: fullMap,
        });
      };

      const res = await tryPartialUpdate();
      let updated = res?.data?.data;
      let updatedMap = extractEmotionMap(updated?.emotions);

      const likelyOverwroteOtherEmotions = emotions.some((e) => {
        if (e === emotion) return false;
        const beforeCount = fullMap[e]?.length ?? 0;
        const afterCount = updatedMap[e]?.length ?? 0;
        return beforeCount > 0 && afterCount === 0;
      });

      if (likelyOverwroteOtherEmotions) {
        const res2 = await tryFullUpdate();
        updated = res2?.data?.data;
        updatedMap = extractEmotionMap(updated?.emotions);
      }

      const normalized = normalizeVocabularyToWords(updatedMap as VocabularyApiPayload);
      setWords(normalized);
      setEditContents((prev) => ({
        ...prev,
        [emotion]: (updatedMap[emotion] || []).join(", "),
      }));
    } catch (err: any) {
      // If partial update fails (e.g., backend requires full payload), try full update once.
      try {
        const res = await api.put<UpdateVocabularyResponse>(UPDATE_ENDPOINT, {
          emotions: fullMap,
        });
        const updated = res?.data?.data;
        const updatedMap = extractEmotionMap(updated?.emotions);
        const normalized = normalizeVocabularyToWords(
          updatedMap as VocabularyApiPayload,
        );
        setWords(normalized);
        setEditContents((prev) => ({
          ...prev,
          [emotion]: (updatedMap[emotion] || []).join(", "),
        }));
      } catch (err2: any) {
        console.error("Failed to add words", err2);
        setLoadError(
          err2?.response?.data?.message ||
            err2?.message ||
            "Failed to update vocabulary",
        );
      }
    } finally {
      setIsSaving((prev) => ({ ...prev, [emotion]: false }));
    }
  };

  const toggleExpand = (emotion: EmotionType) => {
    setExpandedEmotion(expandedEmotion === emotion ? null : emotion);
  };

  const hasActiveFilters = searchTerm || filterLanguage !== "all";

  // Get word count for an emotion (ignoring filters for header)
  const getRawWordCount = (emotion: EmotionType) => {
    return words.filter((w) => w.emotion === emotion).length;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-purple-500" />
            Emotion Vocabulary Manager
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Click on any emotion card to edit its vocabulary (comma-separated
            format)
          </p>
          {loadError ? (
            <p className="text-sm text-red-600 mt-2">{loadError}</p>
          ) : null}
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <button
            type="button"
            onClick={() => fetchVocabulary()}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                Loading...
              </>
            ) : (
              "Refresh"
            )}
          </button>
        </div>
      </div>

      {/* Emotion Cards Grid */}
      <div className="space-y-4">
        {emotions.map((emotion) => {
          const config = emotionConfig[emotion];
          const Icon = config.icon;
          const isExpanded = expandedEmotion === emotion;
          const wordCount = getRawWordCount(emotion);

          return (
            <div
              key={emotion}
              className={`rounded-2xl border transition-all duration-300 overflow-hidden ${config.borderColor} ${isExpanded ? "shadow-lg" : "shadow-sm hover:shadow-md"}`}
            >
              {/* Emotion Header - Clickable */}
              <button
                type="button"
                onClick={() => toggleExpand(emotion)}
                className={`w-full flex items-center justify-between p-2 ${config.bgColor} hover:bg-opacity-80 transition-all`}
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center bg-white shadow-sm ${config.color}`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="text-md font-bold text-gray-900">
                        {config.label}
                      </span>
                      <span className="text-lg ml-1">{config.emoji}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-sm font-medium ${config.color}`}>
                        {wordCount} {wordCount === 1 ? "word" : "words"}
                      </span>
                    </div>
                  </div>
                </div>
                <div
                  className={`transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                >
                  {isExpanded ? (
                    <ChevronUp className={`w-5 h-5 ${config.color}`} />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>

              {/* Expanded Content - Textarea Editor */}
              {isExpanded && (
                <div className="p-5 bg-white animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="mb-4 p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Add words to {config.label} (comma-separated)
                    </label>
                    <div className="flex flex-col gap-3">
                      <textarea
                        value={editContents[emotion] || ""}
                        onChange={(e) =>
                          setEditContents((prev) => ({
                            ...prev,
                            [emotion]: e.target.value,
                          }))
                        }
                        placeholder={`e.g., sad, unhappy, depressed, เศร้า, เหงา, หม่นไหม`}
                        rows={6}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-y font-mono text-sm"
                        autoFocus={isExpanded}
                      />
                      <p className="text-xs text-gray-500">
                        Existing words are shown above in the card count. This
                        action only adds new words (no deletion).
                      </p>
                      <div className="flex gap-2 justify-end">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            void handleSaveEmotionWords(emotion);
                          }}
                          disabled={isSaving[emotion]}
                          className="inline-flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 disabled:opacity-50 transition-all shadow-sm"
                        >
                          {isSaving[emotion] ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4" />
                              Add Words
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VocabularyManager;
